import { Container, Grid, Typography, Box, Card, CardContent, Chip, Button, Avatar } from "@mui/material";
import styled, { keyframes } from "styled-components";
import InnerBanner from "../presentation/InnerBanner";
import SEO from "../global/SEO";
import SchoolIcon from "@mui/icons-material/School";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BookIcon from "@mui/icons-material/Book";
import PublicIcon from "@mui/icons-material/Public";
import VerifiedIcon from "@mui/icons-material/Verified";
import SupportIcon from "@mui/icons-material/Support";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SecurityIcon from "@mui/icons-material/Security";
import StarIcon from "@mui/icons-material/Star";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ResearchIcon from "@mui/icons-material/Science";
import FormatIcon from "@mui/icons-material/FormatSize";

const UKDissertationHelp = () => {
  const services = [
    {
      title: "UK Dissertation Writing",
      description: "Professional dissertation writing services tailored to UK university standards and requirements.",
      features: ["UK academic standards", "Proper referencing", "Original research", "Chapter-wise delivery"],
      icon: <SchoolIcon />,
      color: "#1f2a62",
      gradient: "linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%)"
    },
    {
      title: "UK University Guidelines",
      description: "All dissertations follow specific UK university formatting and citation guidelines.",
      features: ["Harvard referencing", "APA/MLA styles", "Proper formatting", "University compliance"],
      icon: <FormatIcon />,
      color: "#21a5a7",
      gradient: "linear-gradient(135deg, #21a5a7 0%, #1f2a62 100%)"
    },
    {
      title: "Research Methodology",
      description: "Comprehensive research methodology support for UK dissertation requirements.",
      features: ["Qualitative research", "Quantitative analysis", "Literature review", "Data collection"],
      icon: <ResearchIcon />,
      color: "#1f2a62",
      gradient: "linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%)"
    },
    {
      title: "Dissertation Help for Indian Students in UK",
      description: "Specialized support for Indian students studying in UK universities.",
      features: ["Cultural understanding", "UK standards", "WhatsApp support", "Affordable pricing"],
      icon: <PublicIcon />,
      color: "#21a5a7",
      gradient: "linear-gradient(135deg, #21a5a7 0%, #1f2a62 100%)"
    }
  ];

  const features = [
    {
      title: "UK Standards",
      description: "All dissertations follow UK university guidelines and academic standards",
      icon: <VerifiedIcon />,
      color: "#1f2a62"
    },
    {
      title: "Original Research",
      description: "100% original content with comprehensive research and proper citations",
      icon: <SecurityIcon />,
      color: "#21a5a7"
    },
    {
      title: "Expert Writers",
      description: "PhD qualified writers with UK university experience",
      icon: <StarIcon />,
      color: "#1f2a62"
    }
  ];

  const stats = [
    { number: "1000+", label: "Dissertations Completed" },
    { number: "99%", label: "Success Rate" },
    { number: "PhD", label: "Qualified Writers" },
    { number: "100%", label: "Original Content" }
  ];

  return (
    <>
      <SEO 
        title="Dissertation Help UK | Expert Dissertation Writing for UK Universities"
        description="Get custom dissertation help from UK academic experts. 100% original, referenced, and formatted as per university guidelines."
        keywords="dissertation help UK, UK dissertation writing, dissertation help for Indian students, UK university dissertation, academic writing UK, dissertation services UK"
        h1Heading="Professional Dissertation Writing Services for UK Students"
        canonicalUrl="https://liveassignment.com/uk-dissertation-help"
      />
      <InnerBanner pageTitle="UK Dissertation Help"></InnerBanner>
      <Wrapper>
        <Container>
          <HeaderSection>
            <Typography variant="h2" component="h1" align="center" gutterBottom>
              Professional Dissertation Writing Services for UK Students
            </Typography>
            <Typography variant="h6" align="center" color="textSecondary" paragraph>
              Expert dissertation support tailored to UK university standards and requirements
            </Typography>
          </HeaderSection>

          <StatsSection>
            <Grid container spacing={3}>
              {stats.map((stat, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <StatCard>
                    <Typography variant="h3" color="primary" gutterBottom>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" align="center">
                      {stat.label}
                    </Typography>
                  </StatCard>
                </Grid>
              ))}
            </Grid>
          </StatsSection>
          
          <ServicesSection>
            <Typography variant="h4" align="center" gutterBottom>
              Our UK Dissertation Services
            </Typography>
            <Grid container spacing={3}>
              {services.map((service, index) => (
                <Grid item xs={12} sm={6} lg={3} key={index}>
                  <ServiceCard className="service-card">
                    <CardHeader>
                      <IconWrapper gradient={service.gradient}>
                        {service.icon}
                      </IconWrapper>
                      <Typography variant="h6" component="h3" gutterBottom color="primary">
                        {service.title}
                      </Typography>
                    </CardHeader>
                    <CardContent>
                      <Typography variant="body2" paragraph>
                        {service.description}
                      </Typography>
                      <FeaturesList>
                        {service.features.map((feature, idx) => (
                          <Chip 
                            key={idx} 
                            label={feature} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </FeaturesList>
                      <Button 
                        variant="contained" 
                        color="primary" 
                        fullWidth 
                        sx={{ mt: 2 }}
                      >
                        Get Started
                      </Button>
                    </CardContent>
                  </ServiceCard>
                </Grid>
              ))}
            </Grid>
          </ServicesSection>
          
          <FeaturesSection>
            <Typography variant="h4" align="center" gutterBottom>
              Why Choose Our UK Dissertation Help?
            </Typography>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} lg={4} key={index}>
                  <FeatureCard className="feature-card">
                    <FeatureIcon color={feature.color}>
                      {feature.icon}
                    </FeatureIcon>
                    <Typography variant="h6" color="primary" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" align="center">
                      {feature.description}
                    </Typography>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </FeaturesSection>
          
          <TrustSection>
            <Typography variant="h4" gutterBottom align="center">
              Trusted by Indian Students in UK Universities
            </Typography>
            <Typography variant="body1" paragraph align="center">
              We understand the unique challenges faced by Indian students studying in UK universities. 
              Our team provides culturally sensitive support while maintaining the highest UK academic standards.
            </Typography>
            <LocationInfo>
              <LocationOnIcon color="primary" />
              <Typography variant="body2" color="primary">
                Serving students across UK, Australia, Canada & New Zealand
              </Typography>
            </LocationInfo>
          </TrustSection>
        </Container>
      </Wrapper>
    </>
  );
};

export default UKDissertationHelp;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const Wrapper = styled.div`
  padding: 70px 0;
  position: relative;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);

  @media (max-width: 992px) {
    padding: 40px 0;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
  
  .MuiTypography-h2 {
    background: linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
`;

const StatsSection = styled.div`
  margin-bottom: 80px;
  
  .MuiGrid-item {
    display: flex;
  }
`;

const StatCard = styled(Box)`
  text-align: center;
  padding: 30px 20px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  }
  
  .MuiTypography-h3 {
    font-weight: 700;
    background: linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const ServicesSection = styled.div`
  margin-bottom: 80px;
  
  .MuiTypography-h4 {
    margin-bottom: 40px;
    background: linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
  }
  
  .MuiGrid-item {
    display: flex;
    margin-bottom: 20px;
  }
`;

const ServiceCard = styled(Card)`
  height: 100%;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: none;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-10px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }

  .MuiCardContent-root {
    padding: 30px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .MuiTypography-h6 {
    font-weight: 600;
    margin-bottom: 15px;
    text-align: center;
  }

  .MuiChip-root {
    font-size: 12px;
    height: 24px;
  }
`;

const CardHeader = styled.div`
  text-align: center;
  padding: 30px 30px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  background: ${props => props.gradient};
  color: white;
  font-size: 40px;
  animation: ${pulse} 2s infinite;
  
  .MuiSvgIcon-root {
    font-size: 40px;
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 15px 0;
`;

const FeaturesSection = styled.div`
  text-align: center;
  padding: 60px 0;
  
  .MuiTypography-h4 {
    margin-bottom: 40px;
    background: linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
  }
  
  .MuiGrid-item {
    display: flex;
    margin-bottom: 20px;
  }
`;

const FeatureCard = styled(Box)`
  text-align: center;
  padding: 40px 20px;
  border-radius: 20px;
  background: white;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0,0,0,0.15);
  }
  
  .MuiTypography-h6 {
    margin: 20px 0 15px;
    font-weight: 600;
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  background: ${props => props.color};
  color: white;
  font-size: 30px;
  
  .MuiSvgIcon-root {
    font-size: 30px;
  }
`;

const TrustSection = styled.div`
  text-align: center;
  padding: 60px 40px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  margin-top: 60px;
  
  .MuiTypography-h4 {
    background: linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
    margin-bottom: 20px;
  }
`;

const LocationInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  
  .MuiSvgIcon-root {
    font-size: 24px;
  }
`; 